import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import {
  getCachedAuthorization,
  loadAuthorization,
} from "auth/authorization.service";
import {
  getActionColor,
  getActionLabel,
  getModuleDisplayName,
  getResourceDisplayName,
  getRoleColor,
} from "modules/systems/utils/permission.utils";

const cardSx = {
  border: "1px solid #e3e9ef",
  borderRadius: "12px",
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const parsePermissionCode = (permissionCode) => {
  const [moduleCode = "other", resourceCode = "other", actionCode = ""] =
    normalize(permissionCode).split(".");

  return { moduleCode, resourceCode, actionCode };
};

const isApplicationAccessPermission = (permissionCode) =>
  parsePermissionCode(permissionCode).moduleCode === "pms";

const getApplicationAccessCode = (application) =>
  normalize(
    application?.accessPermissionCode ||
      application?.AccessPermissionCode ||
      application?.applicationCode ||
      application?.ApplicationCode
  );

const PermissionsTab = ({ user }) => {
  const [authorization, setAuthorization] = useState(() =>
    getCachedAuthorization()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const loadAccessScope = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await loadAuthorization({ force: true });
        if (active) setAuthorization(data);
      } catch (loadError) {
        console.error("Error loading authorization:", loadError);
        if (active && !getCachedAuthorization()) {
          setError("Không thể tải thông tin phân quyền của tài khoản.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAccessScope();
    return () => {
      active = false;
    };
  }, [user?.userId]);

  const { applicationAccess, modulePermissionGroups, permissionCount } =
    useMemo(() => {
      const roles = authorization?.roles || [];
      const applications = authorization?.applications || [];
      const permissions = authorization?.permissions || [];
      const accessPermissionCodes = new Set(
        permissions
          .filter(isApplicationAccessPermission)
          .map(normalize)
      );

      const applicationItems = applications.map((application) => {
        const applicationId =
          application.applicationId ?? application.ApplicationId;
        const accessCode = getApplicationAccessCode(application);

        return {
          application,
          accessCode,
          hasAccessPermission: accessPermissionCodes.has(accessCode),
          roles: roles.filter(
            (role) =>
              Number(role.applicationId ?? role.ApplicationId) ===
              Number(applicationId)
          ),
        };
      });

      const groupedPermissions = new Map();
      permissions
        .filter((permission) => !isApplicationAccessPermission(permission))
        .forEach((permission) => {
          const { moduleCode } = parsePermissionCode(permission);
          if (!groupedPermissions.has(moduleCode)) {
            groupedPermissions.set(moduleCode, new Map());
          }
          groupedPermissions
            .get(moduleCode)
            .set(normalize(permission), permission);
        });

      const moduleGroups = Array.from(groupedPermissions.entries())
        .map(([moduleCode, permissionMap]) => {
          const permissionsByResource = new Map();

          Array.from(permissionMap.values()).forEach((permission) => {
            const { resourceCode, actionCode } = parsePermissionCode(permission);
            if (!permissionsByResource.has(resourceCode)) {
              permissionsByResource.set(resourceCode, []);
            }
            permissionsByResource.get(resourceCode).push({
              permission,
              actionCode,
            });
          });

          const functionGroups = Array.from(permissionsByResource.entries())
            .map(([resourceCode, actions]) => ({
              resourceCode,
              resourceName: getResourceDisplayName(resourceCode),
              actions: actions.sort((a, b) =>
                getActionLabel(a.actionCode).localeCompare(
                  getActionLabel(b.actionCode),
                  "vi"
                )
              ),
            }))
            .sort((a, b) => a.resourceName.localeCompare(b.resourceName, "vi"));

          return {
            moduleCode,
            moduleName: getModuleDisplayName(moduleCode),
            permissions: Array.from(permissionMap.values()),
            functionGroups,
          };
        })
        .sort((a, b) => a.moduleName.localeCompare(b.moduleName, "vi"));

      return {
        applicationAccess: applicationItems,
        modulePermissionGroups: moduleGroups,
        permissionCount: permissions.length,
      };
    }, [authorization]);

  if (loading && !authorization) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Card variant="outlined" sx={cardSx}>
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              mb: 2.5,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box sx={{ width: 42, height: 42, borderRadius: "10px", bgcolor: "#eaf3fb", color: "primary.main", display: "grid", placeItems: "center" }}>
                <AppsRoundedIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Ứng dụng được truy cập
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Các quyền có cấu trúc pms.*.access
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" gap={1}>
              <Chip label={`${applicationAccess.length} ứng dụng`} size="small" color="success" variant="outlined" />
              <Chip label={`${permissionCount} quyền hiệu lực`} size="small" variant="outlined" />
            </Stack>
          </Box>

          {applicationAccess.length === 0 ? (
            <Alert severity="warning">
              Tài khoản chưa được cấp quyền truy cập ứng dụng nào.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {applicationAccess.map(({ application, accessCode, hasAccessPermission, roles }) => {
                const applicationId = application.applicationId ?? application.ApplicationId;
                const applicationName =
                  application.applicationName ||
                  application.ApplicationName ||
                  application.applicationCode ||
                  application.ApplicationCode;

                return (
                  <Grid size={{ xs: 12, md: 6 }} key={applicationId}>
                    <Box sx={{ height: "100%", p: 2, border: "1px solid #e6ebf0", borderRadius: "10px", bgcolor: "#f8fafc", boxSizing: "border-box" }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {applicationName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {accessCode}
                          </Typography>
                        </Box>
                        <CheckCircleRoundedIcon color={hasAccessPermission ? "success" : "disabled"} fontSize="small" />
                      </Box>

                      <Box display="flex" alignItems="center" gap={0.75} mt={1.5} mb={1}>
                        <ManageAccountsRoundedIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Vai trò trong ứng dụng
                        </Typography>
                      </Box>
                      <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                        {roles.length > 0 ? (
                          roles.map((role) => (
                            <Chip
                              key={role.roleId ?? role.RoleId ?? role.roleCode}
                              label={role.roleName || role.roleCode}
                              size="small"
                              sx={{
                                color: getRoleColor(role.roleName),
                                borderColor: getRoleColor(role.roleName),
                              }}
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Chip label="Quyền gán trực tiếp" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {modulePermissionGroups.map(
        ({ moduleCode, moduleName, permissions, functionGroups }) => (
        <Card key={moduleCode} variant="outlined" sx={cardSx}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <SecurityRoundedIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {moduleName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ModuleCode: {moduleCode}
                  </Typography>
                </Box>
              </Box>
              <Chip label={`${permissions.length} quyền`} size="small" color="primary" variant="outlined" />
            </Box>

            <Stack gap={1.25}>
              {functionGroups.map(({ resourceCode, resourceName, actions }) => (
                <Box
                  key={resourceCode}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    p: 1.25,
                    border: "1px solid #eef1f4",
                    borderRadius: "8px",
                    bgcolor: "#f8fafc",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      minWidth: { sm: 210 },
                      pt: { sm: 0.35 },
                    }}
                  >
                    {resourceName}:
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                    {actions.map(({ permission, actionCode }) => (
                      <Chip
                        key={permission}
                        label={getActionLabel(actionCode)}
                        title={permission}
                        size="small"
                        color={getActionColor(actionCode)}
                        variant="outlined"
                        sx={{ bgcolor: "#fff" }}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ))}

      {applicationAccess.length > 0 && modulePermissionGroups.length === 0 && (
        <Alert severity="info">
          Tài khoản có quyền truy cập ứng dụng nhưng chưa có quyền chức năng.
        </Alert>
      )}
    </Box>
  );
};

export default PermissionsTab;
